import * as React from 'react';
import {center, centerCenter, endJustified, horizontal, horizontallySpaced, startJustified} from 'csstips';
import {em, important, percent, px, rem} from 'csx';
import {classes, style} from 'typestyle';
import {colorBrand, colorContrast, colorSlate, colorSubtle, colorWhite} from '../theme/color';
import {ArtistLinkList} from './artist-link-list';
import {Duration} from './duration';
import {observer} from 'mobx-react';
import {resetAnchorStyles} from '../theme/reset';
import {transitionQuickEase} from '../theme/transition';
import {Highlight} from './highlight';
import {TrackSchema} from '../types/schema';
import {ImagePreloader} from '../types/fn';
import {ButtonInfo} from './button-info';
import {ConnectedTrackInfo} from './track/info';
import {PlayButtonCompat} from './compat/play-button-compat';
import Waveform from './player/waveform';

export interface ReleaseTrackProps {
  track: TrackSchema;
  isMobile: boolean;
  preloadWaveform: ImagePreloader;
  PlayButton: any;
  AddRemove: any;
  BrandSponsoredLabel: any;
  Favourite: any;
  onClickLink(event: React.MouseEvent<any>): any;
  hideMeta?: boolean;
  hideControls?: boolean;
  highlight?: string;
  isBasket?: boolean;
  invert?: boolean;
  hideInfo?: boolean;
}

type State = {
  showInfo: boolean;
}

const ONE_WEEK = 1000 * 60 * 60 * 24 * 7;

@observer
export class ReleaseTrack extends React.Component<ReleaseTrackProps, State> {
  state: State = {
    showInfo: false,
  };

  render() {
    const { invert, track, highlight, hideInfo, isMobile, isBasket, PlayButton, Favourite, AddRemove, BrandSponsoredLabel, onClickLink, preloadWaveform, hideMeta, hideControls } = this.props;
    const { showInfo } = this.state;

    const highlightTitle = track.highlight && track.highlight.length
      && track.highlight.find(h => h.key === 'title');

    const isNew = track.created_at && (Date.now() - new Date(track.created_at).getTime()) < ONE_WEEK;

    const infoClass = classes(
      ReleaseTrack.styles.infoContainer,
      !hideInfo && showInfo
        ? ReleaseTrack.styles.infoContainerShow
        : ReleaseTrack.styles.infoContainerHidden
    );

    const auditionDuration = typeof track.audition_point !== 'undefined' && track.audition_point !== null
      ? track.audition_point * 1000
      : null;

    const auditionStyle = track.duration && auditionDuration !== null
      ? {left: `${Math.min(100, 100 * auditionDuration / track.duration)}%`}
      : null;

    return (
      <div>
        <div className={ReleaseTrack.styles.container}>
          <div className={!isMobile ? ReleaseTrack.styles.playContainer : ReleaseTrack.styles.playContainerNarrow}>
            <PlayButton
              invert={invert}
              track={track}
              className={ReleaseTrack.styles.play}
              color={colorBrand.toString()}
            />
          </div>
          {!hideMeta && (
            <div className={classes(ReleaseTrack.styles.metaContainer, isMobile && ReleaseTrack.styles.metaContainerNarrow)}>
              <div className={ReleaseTrack.styles.titleContainer}>
                {isNew && !isBasket && (
                  <span className={ReleaseTrack.styles.newTag}>NEW</span>
                )}
                <a
                  className={invert ? ReleaseTrack.styles.linkInverted : ReleaseTrack.styles.link}
                  href={`/music/artists/${track.artists[0].slug}/track/${track.slug}`}
                  onClick={onClickLink}
                >
                  <Highlight
                    innerHtml={highlightTitle ? highlightTitle.value : void 0}
                    match={highlight}
                  >{track.title}</Highlight>
                </a>
              </div>
              <ArtistLinkList
                className={ReleaseTrack.styles.artists}
                highlight={highlight}
                model={{ artists: track.artists }}
                onClickLink={onClickLink}
                linkClassName={ReleaseTrack.styles.linkArtist}
              />
            </div>
          )}
          {!isMobile && (
            <div className={!isMobile ? ReleaseTrack.styles.waveformContainer : ReleaseTrack.styles.waveformContainerNarrow}>
              {auditionStyle && (
                <PlayButtonCompat
                  size={18}
                  startAt={auditionDuration}
                  style={auditionStyle}
                  className={ReleaseTrack.styles.audition}
                  track={track}
                />
              )}
              {track.audio && track.audio.identity && (
                  <Waveform
                    waveform={track.audio.identity}
                    preloadWaveform={preloadWaveform}
                    duration={track.duration}
                    end={track.duration}
                    start={auditionDuration ? auditionDuration : 0}
                  />
              )}
            </div>
          )}
          {!isMobile && (
            <div className={ReleaseTrack.styles.durationContainer}>
              <Duration value={track.duration}/>
            </div>
          )}
          {!isMobile && (
            <div className={ReleaseTrack.styles.brandedContainer}>
              {track.brand_sponsored && (
                <BrandSponsoredLabel invert={invert}/>
              )}
            </div>
          )}
          {!hideControls && !isBasket && (
            <div className={!isMobile ? ReleaseTrack.styles.controlsContainer : ReleaseTrack.styles.controlsContainerNarrow}>
              {isMobile && track.brand_sponsored && (
                <BrandSponsoredLabel invert={invert}/>
              )}
              <ButtonInfo onClick={this.handleClickInfo} isActive={showInfo} />
              <Favourite track={track}/>
              <AddRemove track={track} withLicense={!isMobile} reverse={true}/>
            </div>
          )}
          {isBasket && (
            <div className={!isMobile ? ReleaseTrack.styles.controlsContainer : ReleaseTrack.styles.controlsContainerNarrow}>
              {isMobile && track.brand_sponsored && (
                <BrandSponsoredLabel invert={invert}/>
              )}
              <AddRemove track={track} withLicense={!isMobile} reverse={true} invert={invert} removeOnly={true}/>
            </div>
          )}
        </div>
          <div className={infoClass}>
            {!hideInfo && showInfo && (
              <ConnectedTrackInfo
                identity={track.identity}
              />
            )}
          </div>
      </div>
    )
  }

  handleClickInfo = (event: React.MouseEvent<any>) => {
    const show = !this.state.showInfo;

    this.setState({
      showInfo: show
    });
  };

  static styles = {
    container: style({
      ...horizontal,
      ...horizontallySpaced(16),
      ...center,
      position: 'relative',
      overflow: 'hidden',
      padding: '8px 0',
      transition: 'padding 0.075s ease-in',
    }),
    infoContainer: style({
      transition: 'opacity 0.075s ease-in'
    }),
    infoContainerHidden: style({
      opacity: 0,
      pointerEvents: 'none'
    }),
    infoContainerShow: style({
      opacity: 1,
    }),
    link: style({
      color: colorSlate.toString(),
      textDecoration: 'underline',
      $nest: { ...resetAnchorStyles(colorSlate.toString()) }
    }),
    linkInverted: style({
      color: colorWhite.toString(),
      textDecoration: 'underline',
      $nest: { ...resetAnchorStyles(colorWhite.toString()) }
    }),
    linkArtist: style({
      fontSize: px(12),
      color: colorSubtle.toString(),
      $nest: { ...resetAnchorStyles(colorSubtle.toString()) }
    }),
    artists: style({
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'pre'
    }),
    play: style({

    }),
    playContainer: style({
      paddingLeft: px(1)
    }),
    playContainerNarrow: style({
      minWidth: px(28),
      ...horizontal,
      ...startJustified
    }),
    metaContainer: style({
      lineHeight: em(1.1),
      fontSize: px(16),
      flex: 1,
      overflow: 'hidden',
      maxWidth: px(220),
    }),
    titleContainer: style({
      overflow: 'hidden',
      whiteSpace: 'pre',
      textOverflow: 'ellipsis'
    }),
    metaContainerNarrow: style({
      flex: important(3),
      maxWidth: important('none'),
    }),
    waveformContainer: style({
      ...centerCenter,
      position: 'relative',
      flex: 1,
      $nest: {
        '&': {
          display: 'block',
          zIndex: 1,
          width: percent(100),
          height: px(30),
          borderRadius: px(50),
          transition: `transform ${transitionQuickEase}`
        },
        '& :hover': {
          transform: 'scale(1, 1.1)',
        },
        '& img': {
          width: percent(100),
          height: px(30),
          borderRadius: px(50)
        }
      }
    }),
    waveformContainerNarrow: style({
      display: 'none'
    }),
    audition: style({
      position: 'absolute',
      zIndex: 2,
      bottom: 0,
      left: 0,
      margin: '0 0 -8px -9px',
      $nest: {
        '&:hover::after': {
          display: 'block',
          content: '"Similar"',
          textTransform: 'uppercase',
          position: 'absolute',
          left: percent(100),
          fontWeight: 500,
          bottom: 0,
          fontSize: rem(0.625),
          whiteSpace: 'pre',
          margin: '0 0 -4px 6px',
          color: colorSubtle.toString()
        },
        '&:hover::before': {
          position: 'absolute',
          display: 'block',
          content: '""',
          bottom: percent(100),
          top: px(-24),
          zIndex: 1,
          width: rem(0.125),
          borderRadius: rem(0.0625),
          background: colorBrand.toString()
        }
      }
    }),
    durationContainer: style({
      minWidth: px(50),
      maxWidth: px(70),
      ...horizontal,
      ...endJustified,
    }),
    durationContainerNarrow: style({
      minWidth: px(50),
      maxWidth: px(70),
      ...horizontal,
      ...endJustified,
    }),
    brandedContainer: style({
      minWidth: px(20),
      maxWidth: px(20),
      ...horizontal,
      ...endJustified,
    }),
    brandedContainerNarrow: style({
      minWidth: px(20),
      maxWidth: px(20),
      marginRight: important('5px'),
      ...horizontal,
      ...endJustified,
    }),
    controlsContainer: style({
      ...horizontal,
      ...horizontallySpaced(5),
      ...endJustified,
      minWidth: px(64),
      paddingRight: px(1)
    }),
    controlsContainerNarrow: style({
      ...horizontal,
      ...horizontallySpaced(5),
      ...endJustified,
      minWidth: px(64),
      paddingRight: px(1)
    }),
    newTag: style({
      background: colorContrast.toString(),
      borderRadius: px(4),
      fontSize: px(9),
      lineHeight: px(16),
      padding: '0 4px',
      margin: '0 3px 0 0',
      display: 'inline-block',
      transform: 'translateY(-2px)',
      color: colorWhite.toString()
    })
  }
}
